import React, { useState } from "react";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { Team, TeamMember, Collaborator, TeamRole } from "../../types/team";
import { writeTeamActivity } from "../../config/teamActivity";
import { writeNotification } from "../../hooks/useTeamNotifications";
import store from "../../setup/redux/Store";

// ─── Role config ──────────────────────────────────────────────────────────────

const ROLE_OPTIONS: { role: TeamRole; label: string; description: string; bg: string; color: string }[] = [
  {
    role: "strategist",
    label: "Strategist",
    description: "Can fill market, customer & product steps",
    bg: "#EDE9FE",
    color: "#6D28D9",
  },
  {
    role: "executor",
    label: "Executor",
    description: "Can fill business model & validation steps",
    bg: "#D1FAE5",
    color: "#065F46",
  },
  {
    role: "viewer",
    label: "Viewer",
    description: "Read-only access to all step data",
    bg: "#F1F5F9",
    color: "#475569",
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  team: Team;
  ownerUID: string;            // Currently logged-in user (project owner)
  projectID: string;
  projectTitle: string;
  currentCollaborators: Collaborator[];
  onClose: () => void;
  onShared: (updatedCollaborators: Collaborator[]) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ShareProjectModal({
  team,
  ownerUID,
  projectID,
  projectTitle,
  currentCollaborators,
  onClose,
  onShared,
}: Props) {
  // Non-owner members — the owner already has full access
  const shareableMembers = team.members.filter((m) => m.uid !== ownerUID);

  // Per-member state: selected checkbox + role
  const [selections, setSelections] = useState<Record<string, { checked: boolean; role: TeamRole }>>(
    () => {
      const init: Record<string, { checked: boolean; role: TeamRole }> = {};
      shareableMembers.forEach((m) => {
        const existing = currentCollaborators.find((c) => c.uid === m.uid);
        init[m.uid] = {
          checked: !!existing,
          role: existing?.role ?? "viewer",
        };
      });
      return init;
    }
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = (uid: string) => {
    setSelections((prev) => ({
      ...prev,
      [uid]: { ...prev[uid]!, checked: !prev[uid]!.checked },
    }));
  };

  const handleRoleChange = (uid: string, role: TeamRole) => {
    setSelections((prev) => ({
      ...prev,
      [uid]: { ...prev[uid]!, role },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const newCollaborators: Collaborator[] = shareableMembers
        .filter((m) => selections[m.uid]?.checked)
        .map((m) => ({
          uid: m.uid,
          email: m.email,
          role: selections[m.uid]?.role ?? "viewer",
          joinedAt: new Date().toISOString(),
        }));

      const collaboratorUIDs = newCollaborators.map((c) => c.uid);
      // viewerUIDs is denormalized for Firestore security rules: viewer-role
      // collaborators are blocked from project/progress writes at the rules layer.
      const viewerUIDs = newCollaborators.filter((c) => c.role === "viewer").map((c) => c.uid);

      const projectRef = doc(db, "users", ownerUID, "projects", projectID);
      await updateDoc(projectRef, {
        collaborators: newCollaborators,
        collaboratorUIDs,
        viewerUIDs,
        teamId: newCollaborators.length > 0 ? team.id : null,
      });

      // If sharing with at least one member, add projectID to team.projectIds
      const teamRef = doc(db, "teams", team.id);
      if (newCollaborators.length > 0) {
        await updateDoc(teamRef, { projectIds: arrayUnion(projectID) });
      } else {
        // No collaborators — remove project from team
        await updateDoc(teamRef, { projectIds: arrayRemove(projectID) });
      }

      // Write team activity + global notifications (fire-and-forget)
      if (newCollaborators.length > 0) {
        const currentUser = store.getState().auth?.user;
        const actorEmail = (currentUser as any)?.email ?? "";
        writeTeamActivity(team.id, {
          type: "project_shared",
          actorUID: ownerUID,
          actorEmail,
          projectID,
          projectTitle,
        });

        // Notify each newly added collaborator
        const previousUIDs = new Set(currentCollaborators.map((c) => c.uid));
        for (const collab of newCollaborators) {
          if (!previousUIDs.has(collab.uid)) {
            writeNotification(
              collab.uid,
              `project_shared_${projectID}_from_${ownerUID}`,
              {
                type: "project_shared",
                message: `"${projectTitle}" has been shared with you`,
                read: false,
                createdAt: new Date().toISOString(),
                projectID,
                actorEmail,
                linkPath: `/project/${projectID}`,
              }
            );
          }
        }
      }

      onShared(newCollaborators);
    } catch (err) {
      console.error("Failed to share project:", err);
      setError("Failed to update sharing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const anySelected = shareableMembers.some((m) => selections[m.uid]?.checked);
  const hasChanges = shareableMembers.some((m) => {
    const existing = currentCollaborators.find((c) => c.uid === m.uid);
    const sel = selections[m.uid];
    if (!sel) return false;
    const wasSelected = !!existing;
    const wasRole = existing?.role ?? "viewer";
    return sel.checked !== wasSelected || (sel.checked && sel.role !== wasRole);
  });

  return (
    /* Overlay */
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.45)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal card */}
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
          width: "100%",
          maxWidth: 520,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: "1px solid #F1F5F9",
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              background: "#EDE9FE",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i className="bi bi-people-fill" style={{ fontSize: 16, color: "#6D28D9" }} />
          </div>
          <div className="flex-grow-1">
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0F172A",
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              Share with Team
            </h2>
            <p style={{ fontSize: 13, color: "#475569", margin: "3px 0 0" }}>
              {projectTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px 8px",
              color: "#94A3B8",
              fontSize: 20,
              lineHeight: 1,
              flexShrink: 0,
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {shareableMembers.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 16px",
                color: "#94A3B8",
                fontSize: 14,
              }}
            >
              <i className="bi bi-people" style={{ fontSize: 28, display: "block", marginBottom: 10 }} />
              No team members to share with yet.
              <br />
              Invite members from the Team page first.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {shareableMembers.map((member) => {
                const sel = selections[member.uid];
                const initials = member.email.slice(0, 2).toUpperCase();
                const isChecked = sel?.checked ?? false;

                return (
                  <div
                    key={member.uid}
                    style={{
                      border: `1px solid ${isChecked ? "#8B5CF6" : "#E2E8F0"}`,
                      borderRadius: 10,
                      padding: "12px 14px",
                      background: isChecked ? "#F5F3FF" : "#FFFFFF",
                      transition: "all 150ms ease-out",
                    }}
                  >
                    {/* Member row */}
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggle(member.uid)}
                        style={{
                          width: 16,
                          height: 16,
                          accentColor: "#8B5CF6",
                          flexShrink: 0,
                          cursor: "pointer",
                        }}
                        id={`member-${member.uid}`}
                      />
                      {/* Avatar */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: "#DBEAFE",
                          color: "#1D4ED8",
                          fontSize: 11,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>
                      {/* Email + role chip */}
                      <label
                        htmlFor={`member-${member.uid}`}
                        style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#0F172A",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {member.email}
                        </div>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 1 }}>
                          Team {member.role}
                        </div>
                      </label>
                    </div>

                    {/* Role selector — only shown when checked */}
                    {isChecked && (
                      <div
                        style={{
                          marginTop: 10,
                          paddingTop: 10,
                          borderTop: "1px solid #EDE9FE",
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#6D28D9",
                            alignSelf: "center",
                            marginRight: 2,
                          }}
                        >
                          Access:
                        </span>
                        {ROLE_OPTIONS.map(({ role, label, description, bg, color }) => {
                          const isActive = sel?.role === role;
                          return (
                            <button
                              key={role}
                              type="button"
                              onClick={() => handleRoleChange(member.uid, role)}
                              title={description}
                              style={{
                                background: isActive ? bg : "#F8FAFC",
                                color: isActive ? color : "#94A3B8",
                                border: `1px solid ${isActive ? color + "44" : "#E2E8F0"}`,
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "4px 10px",
                                cursor: "pointer",
                                transition: "all 100ms ease-out",
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                background: "#FEE2E2",
                border: "1px solid #FCA5A5",
                borderRadius: 8,
                fontSize: 13,
                color: "#991B1B",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <i className="bi bi-exclamation-circle" style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid #F1F5F9",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            className="btn btn-light"
            onClick={onClose}
            disabled={saving}
            style={{ fontWeight: 600, fontSize: 13 }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || (!hasChanges && shareableMembers.length > 0)}
            style={{
              fontWeight: 600,
              fontSize: 13,
              background: "#8B5CF6",
              border: "none",
              opacity: saving || (!hasChanges && shareableMembers.length > 0) ? 0.6 : 1,
            }}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Saving…
              </>
            ) : anySelected ? (
              <>
                <i className="bi bi-people-fill me-2" />
                Update Sharing
              </>
            ) : (
              <>
                <i className="bi bi-person-x me-2" />
                Remove All Access
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
